// C:\IntegraNube_WebStore\src\pages\Productos.js
'use strict';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Link as RouterLink,
  useSearchParams,
} from 'react-router-dom';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';

import {
  CloseOutlined as CloseIcon,
  FilterAltOutlined as FilterIcon,
  Inventory2Outlined as ProductIcon,
  OpenInNewOutlined as OpenInNewIcon,
  RestartAltOutlined as ResetIcon,
  SearchOutlined as SearchIcon,
  StarRounded as StarIcon,
  TuneOutlined as TuneIcon,
} from '@mui/icons-material';

import api from '../api/api';
import mercadoLibreIcon from '../assets/mercadolibre.png';

/* ===================================================================== */
/*  Constantes                                                           */
/* ===================================================================== */

const PAGE_LIMIT = 12;

const DEFAULT_FILTERS = Object.freeze({
  q: '',
  categoriaId: '',
  marcaId: '',
  precioMin: '',
  precioMax: '',
  disponible: false,
  destacado: false,
  orden: 'relevancia',
  page: 1,
});

/* ===================================================================== */
/*  Helpers configuración tienda                                        */
/* ===================================================================== */

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (
      source.data.branding ||
      source.data.nombre
    )
  ) {
    return source.data;
  }

  return source;
}

/* ===================================================================== */
/*  Helpers query string                                                 */
/* ===================================================================== */

function boolFromQuery(value) {
  return ['1', 'true', 'si', 'sí', 'yes'].includes(
    String(value || '')
      .trim()
      .toLowerCase()
  );
}

function positivePage(value) {
  const number = Number.parseInt(value, 10);

  return Number.isInteger(number) && number > 0
    ? number
    : 1;
}

function readFilters(searchParams) {
  return {
    q:
      searchParams.get('q') || '',

    categoriaId:
      searchParams.get('categoria_id') || '',

    marcaId:
      searchParams.get('marca_id') || '',

    precioMin:
      searchParams.get('precio_min') || '',

    precioMax:
      searchParams.get('precio_max') || '',

    disponible:
      boolFromQuery(
        searchParams.get('disponible')
      ),

    destacado:
      boolFromQuery(
        searchParams.get('destacado')
      ),

    orden:
      searchParams.get('orden') ||
      'relevancia',

    page:
      positivePage(
        searchParams.get('page')
      ),
  };
}

function filtersToSearchParams(filters) {
  const params = new URLSearchParams();

  const q =
    String(filters.q || '').trim();

  if (q) {
    params.set('q', q);
  }

  if (filters.categoriaId) {
    params.set(
      'categoria_id',
      String(filters.categoriaId)
    );
  }

  if (filters.marcaId) {
    params.set(
      'marca_id',
      String(filters.marcaId)
    );
  }

  if (
    filters.precioMin !== '' &&
    filters.precioMin !== null
  ) {
    params.set(
      'precio_min',
      String(filters.precioMin)
    );
  }

  if (
    filters.precioMax !== '' &&
    filters.precioMax !== null
  ) {
    params.set(
      'precio_max',
      String(filters.precioMax)
    );
  }

  if (filters.disponible) {
    params.set('disponible', '1');
  }

  if (filters.destacado) {
    params.set('destacado', '1');
  }

  if (
    filters.orden &&
    filters.orden !== 'relevancia'
  ) {
    params.set(
      'orden',
      filters.orden
    );
  }

  if (Number(filters.page) > 1) {
    params.set(
      'page',
      String(filters.page)
    );
  }

  return params;
}

/* ===================================================================== */
/*  Helpers respuesta API                                                */
/* ===================================================================== */

function extractProducts(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.productos)) {
    return payload.productos;
  }

  if (Array.isArray(payload?.data?.productos)) {
    return payload.data.productos;
  }

  return [];
}

function extractFilters(payload) {
  if (
    payload?.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }

  return payload || {};
}

/* ===================================================================== */
/*  Helpers producto                                                     */
/* ===================================================================== */

function formatPrice(
  value,
  moneda = 'CLP'
) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return null;
  }

  try {
    return new Intl.NumberFormat(
      'es-CL',
      {
        style: 'currency',
        currency: moneda || 'CLP',
        maximumFractionDigits:
          moneda === 'CLP'
            ? 0
            : 2,
      }
    ).format(number);
  } catch (_) {
    return (
      '$' +
      Math.round(number)
        .toLocaleString('es-CL')
    );
  }
}

function getProductPriceInfo(product) {
  const moneda =
    product?.moneda || 'CLP';

  const precio = Number(
    product?.precio ??
    product?.precio_bruto ??
    product?.precioDetalle?.bruto ??
    product?.precioNeto ??
    product?.precio_neto
  );

  const precioNormal = Number(
    product?.precioNormal ??
    product?.precioNormalDetalle?.bruto
  );

  const ofertaDeclarada =
    product?.enOferta === true ||
    product?.oferta?.activa === true;

  const enOferta =
    ofertaDeclarada &&
    Number.isFinite(precio) &&
    precio > 0 &&
    Number.isFinite(precioNormal) &&
    precioNormal > precio;

  const ahorro = enOferta
    ? Math.max(
        0,
        precioNormal - precio
      )
    : 0;

  const descuentoPorcentaje =
    enOferta && precioNormal > 0
      ? (ahorro / precioNormal) * 100
      : 0;

  return {
    moneda,
    precio:
      Number.isFinite(precio)
        ? precio
        : null,
    precioNormal:
      enOferta
        ? precioNormal
        : null,
    enOferta,
    ahorro,
    descuentoPorcentaje,
  };
}

function formatDiscountPercentage(value) {
  const number = Number(value);

  if (!Number.isFinite(number) || number <= 0) {
    return null;
  }

  if (number >= 10) {
    return Math.round(number).toLocaleString('es-CL');
  }

  if (number >= 1) {
    return number.toLocaleString('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
  }

  return number.toLocaleString('es-CL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
}

function getProductImage(product) {
  const candidates = [
    product?.imagen,
    product?.imagen_url,
    product?.imagenUrl,
    product?.imagen_publica,
    product?.primera_imagen_publica,
    product?.thumbnail,
  ];

  for (const candidate of candidates) {
    if (
      typeof candidate === 'string' &&
      candidate.trim()
    ) {
      return candidate.trim();
    }
  }

  if (
    Array.isArray(product?.imagenes) &&
    product.imagenes.length
  ) {
    const first =
      product.imagenes[0];

    if (
      typeof first === 'string' &&
      first.trim()
    ) {
      return first.trim();
    }

    if (
      first &&
      typeof first === 'object'
    ) {
      return (
        first.url ||
        first.url_publica ||
        first.src ||
        null
      );
    }
  }

  return null;
}

function normalizeMercadoLibreUrl(value) {
  const url = String(value || '').trim();

  if (!url) {
    return null;
  }

  if (/^http:\/\/articulo\.mercadolibre\.cl\//i.test(url)) {
    return url.replace(/^http:/i, 'https:');
  }

  return url;
}

function getMercadoLibreChannel(product) {
  const channel =
    product?.canalesVenta?.mercadolibre;

  const url = normalizeMercadoLibreUrl(
    channel?.url
  );

  if (
    channel?.disponible !== true ||
    !url
  ) {
    return null;
  }

  return {
    disponible: true,
    url,
  };
}

function getProductPublicUrl(product) {
  const slug =
    String(product?.slug || '')
      .trim();

  /*
   * URL pública/canónica definitiva del producto.
   */
  return slug
    ? `/producto/${encodeURIComponent(slug)}`
    : '/productos';
}

/* ===================================================================== */
/*  ProductCard                                                          */
/* ===================================================================== */

function ProductCard({
  product,
  colorPrimario,
  mostrarStock,
  mostrarSku,
}) {
  const [imageError, setImageError] =
    useState(false);

  const imageUrl =
    getProductImage(product);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const nombre =
    String(
      product?.nombre ||
      'Producto'
    ).trim();

  const sku =
    String(
      product?.sku || ''
    ).trim();

  const priceInfo =
    getProductPriceInfo(product);

  const precio =
    formatPrice(
      priceInfo.precio,
      priceInfo.moneda
    );

  const precioNormal =
    priceInfo.enOferta
      ? formatPrice(
          priceInfo.precioNormal,
          priceInfo.moneda
        )
      : null;

  const descuentoPorcentaje =
    formatDiscountPercentage(
      priceInfo.descuentoPorcentaje
    );

  const stockNumber =
    Number(product?.stock);

  const stockKnown =
    product?.stock !== null &&
    product?.stock !== undefined &&
    Number.isFinite(stockNumber);

  const disponible =
    product?.disponible !== false;

  const destacado =
    product?.destacado === true;

  const mercadolibre =
    getMercadoLibreChannel(product);

  const productoUrl =
    getProductPublicUrl(product);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #e5e7eb',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        transition:
          'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',

        '&:hover': {
          transform:
            'translateY(-3px)',
          borderColor:
            '#d1d5db',
          boxShadow:
            '0 16px 36px rgba(15,23,42,0.08)',
        },
      }}
    >
      <Box
        component={RouterLink}
        to={productoUrl}
        aria-label={`Ver ${nombre}`}
        sx={{
          position: 'relative',
          display: 'block',
          width: '100%',
          aspectRatio: '1 / 1',
          bgcolor: '#f8fafc',
          overflow: 'hidden',
          textDecoration: 'none',
        }}
      >
        {imageUrl && !imageError ? (
          <CardMedia
            component="img"
            image={imageUrl}
            alt={nombre}
            loading="lazy"
            onError={() =>
              setImageError(true)
            }
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              p: 1.5,
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              color: '#9ca3af',
            }}
          >
            <ProductIcon
              sx={{
                fontSize: 58,
                opacity: 0.32,
              }}
            />
          </Box>
        )}

        {destacado ? (
          <Chip
            icon={
              <StarIcon
                sx={{
                  fontSize:
                    '16px !important',
                }}
              />
            }
            label="Destacado"
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              bgcolor: '#fff',
              color: colorPrimario,
              fontWeight: 800,
              border:
                `1px solid ${colorPrimario}30`,
              boxShadow:
                '0 4px 12px rgba(15,23,42,0.06)',
              pointerEvents: 'none',
            }}
          />
        ) : null}

        {!disponible ? (
          <Chip
            label="Agotado"
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              bgcolor:
                'rgba(17,24,39,0.88)',
              color: '#fff',
              fontWeight: 800,
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </Box>

      <CardContent
        sx={{
          width: '100%',
          p: 2,
          pb: 1.25,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        {mostrarSku && sku ? (
          <Typography
            variant="caption"
            sx={{
              mb: 0.5,
              color: '#9ca3af',
              fontWeight: 700,
            }}
          >
            SKU {sku}
          </Typography>
        ) : null}

        {product?.marca?.nombre ? (
          <Typography
            variant="caption"
            sx={{
              mb: 0.4,
              color: '#6b7280',
              fontWeight: 800,
              textTransform:
                'uppercase',
              letterSpacing:
                '0.035em',
            }}
          >
            {product.marca.nombre}
          </Typography>
        ) : null}

        <Typography
          component={RouterLink}
          to={productoUrl}
          sx={{
            color: '#111827',
            fontWeight: 800,
            lineHeight: 1.35,
            minHeight: 44,
            textDecoration: 'none',
            display:
              '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient:
              'vertical',
            overflow: 'hidden',

            '&:hover': {
              color: colorPrimario,
            },
          }}
        >
          {nombre}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {precio ? (
          <Box sx={{ mt: 1.5 }}>
            {priceInfo.enOferta &&
            precioNormal ? (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
                sx={{ mb: 0.45 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#9ca3af',
                    fontWeight: 700,
                    textDecoration:
                      'line-through',
                    textDecorationThickness:
                      '1.5px',
                  }}
                >
                  {precioNormal}
                </Typography>

                {descuentoPorcentaje ? (
                  <Chip
                    label={`${descuentoPorcentaje}% OFF`}
                    size="small"
                    sx={{
                      height: 22,
                      bgcolor: '#ecfdf5',
                      color: '#047857',
                      fontWeight: 900,
                      fontSize: 11,
                      '& .MuiChip-label': {
                        px: 0.9,
                      },
                    }}
                  />
                ) : null}
              </Stack>
            ) : null}

            <Typography
              sx={{
                color:
                  disponible
                    ? colorPrimario
                    : '#6b7280',
                fontSize: 21,
                lineHeight: 1.2,
                fontWeight: 900,
              }}
            >
              {precio}
            </Typography>
          </Box>
        ) : null}

        {mostrarStock &&
        stockKnown ? (
          <Typography
            variant="caption"
            sx={{
              mt: 0.8,
              color:
                stockNumber > 0
                  ? '#059669'
                  : '#9ca3af',
              fontWeight: 700,
            }}
          >
            {stockNumber > 0
              ? `${stockNumber.toLocaleString(
                  'es-CL'
                )} disponible${
                  stockNumber === 1
                    ? ''
                    : 's'
                }`
              : 'Sin stock'}
          </Typography>
        ) : null}
      </CardContent>

      <Stack
        spacing={1}
        sx={{
          px: 2,
          pb: 2,
          pt: 0.5,
        }}
      >
        <Button
          component={RouterLink}
          to={productoUrl}
          fullWidth
          variant="contained"
          sx={{
            minHeight: 42,
            bgcolor: colorPrimario,
            color: '#fff',
            textTransform: 'none',
            fontWeight: 900,
            borderRadius: 2,
            boxShadow: 'none',

            '&:hover': {
              bgcolor: colorPrimario,
              filter: 'brightness(0.92)',
              boxShadow: 'none',
            },
          }}
        >
          Ver producto
        </Button>

        {mercadolibre ? (
          <Button
            component="a"
            href={mercadolibre.url}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            variant="contained"
            startIcon={
              <Box
                component="img"
                src={mercadoLibreIcon}
                alt=""
                aria-hidden="true"
                sx={{
                  width: 21,
                  height: 21,
                  objectFit: 'contain',
                }}
              />
            }
            endIcon={<OpenInNewIcon />}
            sx={{
              minHeight: 42,
              bgcolor: '#ffe600',
              color: '#2d3277',
              textTransform: 'none',
              fontWeight: 900,
              borderRadius: 2,
              boxShadow: 'none',

              '&:hover': {
                bgcolor: '#f7dd00',
                boxShadow: 'none',
              },
            }}
          >
            Comprar en Mercado Libre
          </Button>
        ) : null}
      </Stack>
    </Card>
  );
}

/* ===================================================================== */
/*  Panel de filtros                                                     */
/* ===================================================================== */

function FiltersPanel({
  filters,
  filterData,
  localPrecioMin,
  localPrecioMax,
  setLocalPrecioMin,
  setLocalPrecioMax,
  updateFilter,
  applyPrice,
  resetFilters,
  onClose = null,
}) {
  const categorias =
    Array.isArray(filterData?.categorias)
      ? filterData.categorias
      : [];

  const marcas =
    Array.isArray(filterData?.marcas)
      ? filterData.marcas
      : [];

  const destacados =
    Number(
      filterData?.destacados || 0
    );

  const agotados =
    Number(
      filterData?.disponibilidad?.agotados ||
      0
    );

  const precioMinGlobal =
    Number(
      filterData?.precio?.min || 0
    );

  const precioMaxGlobal =
    Number(
      filterData?.precio?.max || 0
    );

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <TuneIcon
            sx={{
              color: '#4b5563',
            }}
          />

          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 18,
              color: '#111827',
            }}
          >
            Filtros
          </Typography>
        </Stack>

        {onClose ? (
          <IconButton
            onClick={onClose}
            aria-label="Cerrar filtros"
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </Stack>

      <Stack spacing={2.25}>
        {categorias.length > 0 ? (
          <FormControl
            fullWidth
            size="small"
          >
            <InputLabel>
              Categoría
            </InputLabel>

            <Select
              value={
                filters.categoriaId
              }
              label="Categoría"
              onChange={(event) =>
                updateFilter(
                  'categoriaId',
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Todas
              </MenuItem>

              {categorias.map(
                (categoria) => (
                  <MenuItem
                    key={categoria.id}
                    value={String(
                      categoria.id
                    )}
                  >
                    {categoria.nombre}
                    {' '}
                    ({Number(
                      categoria.productos ||
                      0
                    )})
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        ) : null}

        {marcas.length > 0 ? (
          <FormControl
            fullWidth
            size="small"
          >
            <InputLabel>
              Marca
            </InputLabel>

            <Select
              value={
                filters.marcaId
              }
              label="Marca"
              onChange={(event) =>
                updateFilter(
                  'marcaId',
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Todas
              </MenuItem>

              {marcas.map(
                (marca) => (
                  <MenuItem
                    key={marca.id}
                    value={String(
                      marca.id
                    )}
                  >
                    {marca.nombre}
                    {' '}
                    ({Number(
                      marca.productos ||
                      0
                    )})
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        ) : null}

        {precioMaxGlobal >
        precioMinGlobal ? (
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 800,
                color: '#374151',
              }}
            >
              Rango de precio
            </Typography>

            <Stack
              direction="row"
              spacing={1}
            >
              <TextField
                size="small"
                type="number"
                label="Mínimo"
                value={localPrecioMin}
                onChange={(event) =>
                  setLocalPrecioMin(
                    event.target.value
                  )
                }
                inputProps={{
                  min: 0,
                }}
              />

              <TextField
                size="small"
                type="number"
                label="Máximo"
                value={localPrecioMax}
                onChange={(event) =>
                  setLocalPrecioMax(
                    event.target.value
                  )
                }
                inputProps={{
                  min: 0,
                }}
              />
            </Stack>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.75,
                color: '#9ca3af',
              }}
            >
              Disponible entre{' '}
              {formatPrice(
                precioMinGlobal
              )}{' '}
              y{' '}
              {formatPrice(
                precioMaxGlobal
              )}
            </Typography>

            <Button
              fullWidth
              size="small"
              variant="outlined"
              onClick={applyPrice}
              sx={{
                mt: 1.25,
                textTransform: 'none',
                fontWeight: 800,
              }}
            >
              Aplicar precio
            </Button>
          </Box>
        ) : null}

        {agotados > 0 ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: '#374151',
                }}
              >
                Sólo disponibles
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: '#9ca3af',
                }}
              >
                Oculta productos agotados
              </Typography>
            </Box>

            <Switch
              checked={
                filters.disponible
              }
              onChange={(event) =>
                updateFilter(
                  'disponible',
                  event.target.checked
                )
              }
            />
          </Stack>
        ) : null}

        {destacados > 0 ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: '#374151',
                }}
              >
                Destacados
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: '#9ca3af',
                }}
              >
                {destacados}{' '}
                producto
                {destacados === 1
                  ? ''
                  : 's'}
              </Typography>
            </Box>

            <Switch
              checked={
                filters.destacado
              }
              onChange={(event) =>
                updateFilter(
                  'destacado',
                  event.target.checked
                )
              }
            />
          </Stack>
        ) : null}

        <Divider />

        <Button
          startIcon={<ResetIcon />}
          onClick={resetFilters}
          sx={{
            justifyContent:
              'flex-start',
            color: '#6b7280',
            textTransform: 'none',
            fontWeight: 800,
          }}
        >
          Limpiar filtros
        </Button>
      </Stack>
    </Box>
  );
}

/* ===================================================================== */
/*  Productos                                                            */
/* ===================================================================== */

export default function Productos({
  tienda = null,
  config = null,
  storeConfig = null,
}) {
  const isMobile =
    useMediaQuery(
      '(max-width:900px)'
    );

  const source =
    tienda ||
    config ||
    storeConfig ||
    {};

  const tiendaData =
    useMemo(
      () =>
        resolveTiendaData(source),
      [source]
    );

  const colorPrimario =
    String(
      tiendaData?.branding
        ?.colorPrimario ||
      '#1976d2'
    ).trim();

  const mostrarStock =
    tiendaData?.ecommerce
      ?.mostrarStock !== false;

  const mostrarSku =
    tiendaData?.ecommerce
      ?.mostrarSku !== false;

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const filters =
    useMemo(
      () =>
        readFilters(searchParams),
      [searchParams]
    );

  const [products, setProducts] =
    useState([]);

  const [meta, setMeta] =
    useState({
      page: 1,
      limit: PAGE_LIMIT,
      total: 0,
      totalPages: 1,
    });

  const [
    filterData,
    setFilterData,
  ] = useState({
    precio: {
      min: 0,
      max: 0,
    },
    categorias: [],
    marcas: [],
    disponibilidad: {
      total: 0,
      disponibles: 0,
      agotados: 0,
    },
    destacados: 0,
    ordenes: [],
  });

  const [loading, setLoading] =
    useState(true);

  const [
    filtersLoading,
    setFiltersLoading,
  ] = useState(true);

  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const [searchText, setSearchText] =
    useState(filters.q);

  const [
    localPrecioMin,
    setLocalPrecioMin,
  ] = useState(filters.precioMin);

  const [
    localPrecioMax,
    setLocalPrecioMax,
  ] = useState(filters.precioMax);

  useEffect(() => {
    setSearchText(filters.q);
  }, [filters.q]);

  useEffect(() => {
    setLocalPrecioMin(
      filters.precioMin
    );

    setLocalPrecioMax(
      filters.precioMax
    );
  }, [
    filters.precioMin,
    filters.precioMax,
  ]);

  const replaceFilters =
    useCallback(
      (
        nextFilters,
        options = {}
      ) => {
        const {
          replace = false,
        } = options;

        setSearchParams(
          filtersToSearchParams(
            nextFilters
          ),
          {
            replace,
          }
        );
      },
      [setSearchParams]
    );

  const updateFilter =
    useCallback(
      (key, value) => {
        replaceFilters({
          ...filters,
          [key]: value,
          page: 1,
        });
      },
      [
        filters,
        replaceFilters,
      ]
    );

  const resetFilters =
    useCallback(() => {
      setDrawerOpen(false);

      replaceFilters({
        ...DEFAULT_FILTERS,
      });
    }, [replaceFilters]);

  useEffect(() => {
    let mounted = true;

    async function loadFilters() {
      setFiltersLoading(true);

      try {
        const response =
          await api.get(
            '/productos/filtros'
          );

        if (!mounted) {
          return;
        }

        setFilterData(
          extractFilters(
            response?.data
          )
        );
      } catch (error) {
        console.warn(
          '[Productos] No fue posible cargar filtros:',
          error?.message || error
        );
      } finally {
        if (mounted) {
          setFiltersLoading(false);
        }
      }
    }

    loadFilters();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const controller =
      new AbortController();

    async function loadProducts() {
      setLoading(true);

      try {
        const params = {
          page: filters.page,
          limit: PAGE_LIMIT,
          orden: filters.orden,
        };

        if (filters.q) {
          params.q =
            filters.q;
        }

        if (filters.categoriaId) {
          params.categoria_id =
            filters.categoriaId;
        }

        if (filters.marcaId) {
          params.marca_id =
            filters.marcaId;
        }

        if (
          filters.precioMin !== ''
        ) {
          params.precio_min =
            filters.precioMin;
        }

        if (
          filters.precioMax !== ''
        ) {
          params.precio_max =
            filters.precioMax;
        }

        if (filters.disponible) {
          params.disponible =
            true;
        }

        if (filters.destacado) {
          params.destacado =
            true;
        }

        const response =
          await api.get(
            '/productos',
            {
              params,
              signal:
                controller.signal,
            }
          );

        if (!mounted) {
          return;
        }

        const payload =
          response?.data || {};

        setProducts(
          extractProducts(payload)
        );

        const nextMeta =
          payload?.meta || {};

        setMeta({
          page:
            Number(
              nextMeta.page ||
              filters.page
            ) || 1,

          limit:
            Number(
              nextMeta.limit ||
              PAGE_LIMIT
            ) || PAGE_LIMIT,

          total:
            Number(
              nextMeta.total ||
              0
            ),

          totalPages:
            Math.max(
              1,
              Number(
                nextMeta.totalPages ||
                nextMeta.totalPaginas ||
                1
              )
            ),
        });
      } catch (error) {
        if (
          error?.name !==
            'CanceledError' &&
          error?.name !==
            'AbortError' &&
          error?.code !==
            'ERR_CANCELED'
        ) {
          console.error(
            '[Productos] Error:',
            error
          );

          if (mounted) {
            setProducts([]);
            setMeta({
              page: 1,
              limit: PAGE_LIMIT,
              total: 0,
              totalPages: 1,
            });
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [
    filters.q,
    filters.categoriaId,
    filters.marcaId,
    filters.precioMin,
    filters.precioMax,
    filters.disponible,
    filters.destacado,
    filters.orden,
    filters.page,
  ]);

  const handleSearchSubmit =
    (event) => {
      event.preventDefault();

      replaceFilters({
        ...filters,
        q:
          String(searchText || '')
            .trim(),
        page: 1,
      });
    };

  const applyPrice = () => {
    const min =
      String(
        localPrecioMin ?? ''
      ).trim();

    const max =
      String(
        localPrecioMax ?? ''
      ).trim();

    const minNumber =
      min === ''
        ? null
        : Number(min);

    const maxNumber =
      max === ''
        ? null
        : Number(max);

    if (
      minNumber !== null &&
      (
        !Number.isFinite(minNumber) ||
        minNumber < 0
      )
    ) {
      return;
    }

    if (
      maxNumber !== null &&
      (
        !Number.isFinite(maxNumber) ||
        maxNumber < 0
      )
    ) {
      return;
    }

    if (
      minNumber !== null &&
      maxNumber !== null &&
      minNumber > maxNumber
    ) {
      return;
    }

    replaceFilters({
      ...filters,

      precioMin:
        minNumber === null
          ? ''
          : String(minNumber),

      precioMax:
        maxNumber === null
          ? ''
          : String(maxNumber),

      page: 1,
    });
  };

  const handlePageChange =
    (_event, nextPage) => {
      replaceFilters({
        ...filters,
        page: nextPage,
      });

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    };

  const activeChips =
    useMemo(() => {
      const result = [];

      if (filters.q) {
        result.push({
          key: 'q',
          label:
            `Búsqueda: ${filters.q}`,
        });
      }

      if (filters.categoriaId) {
        const category =
          filterData?.categorias?.find(
            (item) =>
              String(item.id) ===
              String(
                filters.categoriaId
              )
          );

        result.push({
          key: 'categoriaId',
          label:
            category?.nombre ||
            'Categoría',
        });
      }

      if (filters.marcaId) {
        const brand =
          filterData?.marcas?.find(
            (item) =>
              String(item.id) ===
              String(
                filters.marcaId
              )
          );

        result.push({
          key: 'marcaId',
          label:
            brand?.nombre ||
            'Marca',
        });
      }

      if (
        filters.precioMin !== '' ||
        filters.precioMax !== ''
      ) {
        const min =
          filters.precioMin !== ''
            ? formatPrice(
                filters.precioMin
              )
            : 'Sin mínimo';

        const max =
          filters.precioMax !== ''
            ? formatPrice(
                filters.precioMax
              )
            : 'Sin máximo';

        result.push({
          key: 'precio',
          label:
            `${min} – ${max}`,
        });
      }

      if (filters.disponible) {
        result.push({
          key: 'disponible',
          label:
            'Sólo disponibles',
        });
      }

      if (filters.destacado) {
        result.push({
          key: 'destacado',
          label:
            'Destacados',
        });
      }

      return result;
    }, [
      filters,
      filterData,
    ]);

  const removeChip =
    (key) => {
      if (key === 'precio') {
        replaceFilters({
          ...filters,
          precioMin: '',
          precioMax: '',
          page: 1,
        });

        return;
      }

      const resetValue =
        key === 'disponible' ||
        key === 'destacado'
          ? false
          : '';

      replaceFilters({
        ...filters,
        [key]: resetValue,
        page: 1,
      });
    };

  const filtersPanel = (
    <FiltersPanel
      filters={filters}
      filterData={filterData}
      localPrecioMin={
        localPrecioMin
      }
      localPrecioMax={
        localPrecioMax
      }
      setLocalPrecioMin={
        setLocalPrecioMin
      }
      setLocalPrecioMax={
        setLocalPrecioMax
      }
      updateFilter={
        updateFilter
      }
      applyPrice={
        applyPrice
      }
      resetFilters={
        resetFilters
      }
      onClose={
        isMobile
          ? () =>
              setDrawerOpen(false)
          : null
      }
    />
  );

  return (
    <Box
      sx={{
        bgcolor: '#f8fafc',
        minHeight:
          'calc(100vh - 80px)',
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          py: {
            xs: 3,
            md: 5,
          },
        }}
      >
        <Stack
          spacing={2.5}
          sx={{ mb: 3 }}
        >
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={2}
            alignItems={{
              xs: 'stretch',
              md: 'flex-end',
            }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                component="h1"
                sx={{
                  color: '#111827',
                  fontWeight: 900,
                  fontSize: {
                    xs: 30,
                    md: 40,
                  },
                  letterSpacing:
                    '-0.04em',
                }}
              >
                Productos
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  color: '#6b7280',
                }}
              >
                {loading
                  ? 'Buscando productos...'
                  : `${meta.total.toLocaleString(
                      'es-CL'
                    )} producto${
                      meta.total === 1
                        ? ''
                        : 's'
                    }`}
              </Typography>
            </Box>

            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={1.25}
              alignItems="stretch"
            >
              <Box
                component="form"
                onSubmit={
                  handleSearchSubmit
                }
                sx={{
                  width: {
                    xs: '100%',
                    sm: 330,
                  },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  value={searchText}
                  onChange={(event) =>
                    setSearchText(
                      event.target.value
                    )
                  }
                  placeholder="Buscar productos..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root':
                      {
                        bgcolor: '#fff',
                        borderRadius: 2,
                      },
                  }}
                />
              </Box>

              {isMobile ? (
                <Button
                  variant="outlined"
                  startIcon={
                    <FilterIcon />
                  }
                  onClick={() =>
                    setDrawerOpen(true)
                  }
                  sx={{
                    whiteSpace:
                      'nowrap',
                    textTransform:
                      'none',
                    fontWeight: 800,
                  }}
                >
                  Filtros
                  {activeChips.length
                    ? ` (${activeChips.length})`
                    : ''}
                </Button>
              ) : null}

              <FormControl
                size="small"
                sx={{
                  minWidth: 205,
                }}
              >
                <InputLabel>
                  Ordenar
                </InputLabel>

                <Select
                  value={filters.orden}
                  label="Ordenar"
                  onChange={(event) =>
                    updateFilter(
                      'orden',
                      event.target.value
                    )
                  }
                  sx={{
                    bgcolor: '#fff',
                  }}
                >
                  {(
                    filterData?.ordenes
                      ?.length
                      ? filterData.ordenes
                      : [
                          {
                            value:
                              'relevancia',
                            label:
                              'Relevancia',
                          },
                          {
                            value:
                              'precio_asc',
                            label:
                              'Precio: menor a mayor',
                          },
                          {
                            value:
                              'precio_desc',
                            label:
                              'Precio: mayor a menor',
                          },
                          {
                            value:
                              'nombre_asc',
                            label:
                              'Nombre: A-Z',
                          },
                          {
                            value:
                              'recientes',
                            label:
                              'Más recientes',
                          },
                        ]
                  ).map((item) => (
                    <MenuItem
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {activeChips.length > 0 ? (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
            >
              {activeChips.map(
                (chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    onDelete={() =>
                      removeChip(
                        chip.key
                      )
                    }
                    sx={{
                      bgcolor: '#fff',
                      border:
                        '1px solid #e5e7eb',
                    }}
                  />
                )
              )}

              <Button
                size="small"
                onClick={resetFilters}
                sx={{
                  color: '#6b7280',
                  textTransform:
                    'none',
                  fontWeight: 800,
                }}
              >
                Limpiar todo
              </Button>
            </Stack>
          ) : null}
        </Stack>

        <Grid
          container
          spacing={3}
        >
          {!isMobile ? (
            <Grid
              item
              md={3}
              lg={2.5}
            >
              <Paper
                elevation={0}
                sx={{
                  position: 'sticky',
                  top: 104,
                  p: 2.25,
                  borderRadius: 3,
                  border:
                    '1px solid #e5e7eb',
                  bgcolor: '#fff',
                }}
              >
                {filtersLoading ? (
                  <Stack spacing={2}>
                    <Skeleton height={35} />
                    <Skeleton height={45} />
                    <Skeleton height={45} />
                    <Skeleton height={80} />
                  </Stack>
                ) : (
                  filtersPanel
                )}
              </Paper>
            </Grid>
          ) : null}

          <Grid
            item
            xs={12}
            md={9}
            lg={9.5}
          >
            {loading ? (
              <Grid
                container
                spacing={2.5}
              >
                {Array.from(
                  { length: 8 }
                ).map((_, index) => (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    lg={3}
                    key={index}
                  >
                    <Skeleton
                      variant="rounded"
                      sx={{
                        width: '100%',
                        aspectRatio:
                          '0.72 / 1',
                        borderRadius: 3,
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : products.length > 0 ? (
              <>
                <Grid
                  container
                  spacing={2.5}
                >
                  {products.map(
                    (product) => (
                      <Grid
                        item
                        xs={6}
                        sm={4}
                        lg={3}
                        key={product.id}
                      >
                        <ProductCard
                          product={
                            product
                          }
                          colorPrimario={
                            colorPrimario
                          }
                          mostrarStock={
                            mostrarStock
                          }
                          mostrarSku={
                            mostrarSku
                          }
                        />
                      </Grid>
                    )
                  )}
                </Grid>

                {meta.totalPages > 1 ? (
                  <Stack
                    alignItems="center"
                    sx={{
                      mt: {
                        xs: 4,
                        md: 5,
                      },
                    }}
                  >
                    <Pagination
                      page={meta.page}
                      count={
                        meta.totalPages
                      }
                      onChange={
                        handlePageChange
                      }
                      color="primary"
                      size={
                        isMobile
                          ? 'small'
                          : 'medium'
                      }
                      siblingCount={
                        isMobile
                          ? 0
                          : 1
                      }
                    />
                  </Stack>
                ) : null}
              </>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  py: 8,
                  px: 3,
                  borderRadius: 3,
                  border:
                    '1px solid #e5e7eb',
                  bgcolor: '#fff',
                  textAlign: 'center',
                }}
              >
                <ProductIcon
                  sx={{
                    fontSize: 64,
                    color: '#cbd5e1',
                  }}
                />

                <Typography
                  sx={{
                    mt: 1.5,
                    fontWeight: 900,
                    fontSize: 21,
                    color: '#111827',
                  }}
                >
                  No encontramos productos
                </Typography>

                <Typography
                  sx={{
                    mt: 0.75,
                    color: '#6b7280',
                  }}
                >
                  Prueba quitando algún filtro o realizando otra búsqueda.
                </Typography>

                {activeChips.length > 0 ? (
                  <Button
                    startIcon={
                      <ResetIcon />
                    }
                    onClick={
                      resetFilters
                    }
                    sx={{
                      mt: 2,
                      textTransform:
                        'none',
                      fontWeight: 800,
                      color:
                        colorPrimario,
                    }}
                  >
                    Limpiar filtros
                  </Button>
                ) : null}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() =>
          setDrawerOpen(false)
        }
        PaperProps={{
          sx: {
            width:
              'min(90vw, 360px)',
            p: 2.5,
          },
        }}
      >
        {filtersLoading ? (
          <Stack spacing={2}>
            <Skeleton height={35} />
            <Skeleton height={45} />
            <Skeleton height={45} />
            <Skeleton height={80} />
          </Stack>
        ) : (
          filtersPanel
        )}
      </Drawer>
    </Box>
  );
}
